import { ApiService } from './apiService';
import { API_ENDPOINTS } from '../config/api';
import {
  AdminSubscriptionPlan,
  PlanCurrencyCode,
  PlanDetailResponse,
  PlanDetailUpdate,
  PlanDetailVariant,
  PlanRegionalPriceMap,
  PlanVariant,
  CreatePlanRequest,
  UpdatePlanRequest,
  CreatePlanVariantRequest,
  UpdatePlanVariantRequest,
  PlanDetailForAssignment,
  PriceValue,
} from '../types/api';

type SnakeCaseObject = Record<string, unknown>;

const parseNumericId = (value: unknown, fieldName: string): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.trunc(value);
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  throw new Error(`Invalid ${fieldName}`);
};

const readBoolean = (raw: Record<string, unknown>, camelKey: string, snakeKey: string): boolean =>
  Boolean(raw[camelKey] ?? raw[snakeKey] ?? false);

const readNumber = (raw: Record<string, unknown>, camelKey: string, snakeKey: string): number =>
  Number(raw[camelKey] ?? raw[snakeKey] ?? 0);

const toPriceCell = (input: unknown): PriceValue => {
  if (typeof input === 'number' && Number.isFinite(input)) {
    return { amount: input, taxPercent: 0 };
  }

  if (input && typeof input === 'object') {
    const value = input as {
      amount?: unknown;
      taxPercent?: unknown;
      tax_percent?: unknown;
    };

    const amount =
      typeof value.amount === 'number' && Number.isFinite(value.amount) ? value.amount : 0;
    const taxRaw = value.taxPercent ?? value.tax_percent;
    const taxPercent =
      typeof taxRaw === 'number' && Number.isFinite(taxRaw) ? taxRaw : 0;

    return { amount, taxPercent };
  }

  return { amount: 0, taxPercent: 0 };
};

const pickPriceValue = (source: Record<string, unknown>, keys: string[]): unknown => {
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) {
      return source[key];
    }
  }
  return undefined;
};

const normalizeRegionalPriceMap = (raw: unknown): PlanRegionalPriceMap | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const source = raw as Record<string, unknown>;

  return {
    INR: toPriceCell(pickPriceValue(source, ['INR', 'India', 'IN', '_i_n', 'india', 'in'])),
    USD: toPriceCell(pickPriceValue(source, ['USD', 'US', '_u_s', 'us'])),
    CAD: toPriceCell(pickPriceValue(source, ['CAD', 'Canada', 'CA', '_c_a', 'canada', 'ca'])),
    EUR: toPriceCell(pickPriceValue(source, ['EUR', 'EU', '_e_u', 'eu'])),
  };
};

export const getVariantPriceByCurrency = (
  price: Record<string, unknown> | undefined,
  currency: PlanCurrencyCode
): PriceValue => {
  const normalized = normalizeRegionalPriceMap(price);
  return normalized?.[currency] ?? { amount: 0, taxPercent: 0 };
};

const normalizeAdminSubscriptionPlan = (raw: Record<string, unknown>): AdminSubscriptionPlan => ({
  planId: String(raw.planId ?? raw.plan_id ?? ''),
  name: String(raw.name ?? ''),
  description: String(raw.description ?? ''),
  isHidden: readBoolean(raw, 'isHidden', 'is_hidden'),
  isFree: readBoolean(raw, 'isFree', 'is_free'),
  isDynamic: readBoolean(raw, 'isDynamic', 'is_dynamic'),
  isPopular: readBoolean(raw, 'isPopular', 'is_popular'),
  createdAt:
    typeof raw.createdAt === 'string'
      ? raw.createdAt
      : typeof raw.created_at === 'string'
        ? raw.created_at
        : undefined,
  updatedAt:
    typeof raw.updatedAt === 'string'
      ? raw.updatedAt
      : typeof raw.updated_at === 'string'
        ? raw.updated_at
        : undefined,
  monthlyPrice: normalizeRegionalPriceMap(raw.monthlyPrice ?? raw.monthly_price),
  yearlyPrice: normalizeRegionalPriceMap(raw.yearlyPrice ?? raw.yearly_price),
  activeUserCount: readNumber(raw, 'activeUserCount', 'active_user_count'),
});

const normalizePlanDetailVariant = (raw: Record<string, unknown>): PlanDetailVariant => ({
  planDetailId: parseNumericId(raw.planDetailId ?? raw.plan_detail_id, 'plan detail id'),
  durationMonths: readNumber(raw, 'durationMonths', 'duration_months'),
  price: (raw.price as Record<string, unknown>) ?? {},
  connects: readNumber(raw, 'connects', 'connects'),
  advanceSearch: readBoolean(raw, 'advanceSearch', 'advance_search'),
  limitedSupport: readBoolean(raw, 'limitedSupport', 'limited_support'),
  limitedChat: readBoolean(raw, 'limitedChat', 'limited_chat'),
  limitedChatDays: readNumber(raw, 'limitedChatDays', 'limited_chat_days'),
  highlightedProfile: readBoolean(raw, 'highlightedProfile', 'highlighted_profile'),
  astroMatchmaking: readBoolean(raw, 'astroMatchmaking', 'astro_matchmaking'),
  dedicatedManager: readBoolean(raw, 'dedicatedManager', 'dedicated_manager'),
  dedicatedSeniorManager: readBoolean(raw, 'dedicatedSeniorManager', 'dedicated_senior_manager'),
  arrangedMeetings: readBoolean(raw, 'arrangedMeetings', 'arranged_meetings'),
  hideProfile: readBoolean(raw, 'hideProfile', 'hide_profile'),
  createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : typeof raw.created_at === 'string' ? raw.created_at : undefined,
  updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : typeof raw.updated_at === 'string' ? raw.updated_at : undefined,
});

const normalizePlanDetailResponse = (raw: Record<string, unknown> | null | undefined): PlanDetailResponse => {
  const source = raw ?? {};
  const variantsRaw = (source.variants ?? source.planDetails ?? source.plan_details ?? []) as Record<
    string,
    unknown
  >[];

  return {
    planId: String(source.planId ?? source.plan_id ?? ''),
    name: String(source.name ?? ''),
    description: String(source.description ?? ''),
    isHidden: readBoolean(source, 'isHidden', 'is_hidden'),
    isFree: readBoolean(source, 'isFree', 'is_free'),
    isDynamic: readBoolean(source, 'isDynamic', 'is_dynamic'),
    isPopular: readBoolean(source, 'isPopular', 'is_popular'),
    createdAt: typeof source.createdAt === 'string' ? source.createdAt : undefined,
    updatedAt: typeof source.updatedAt === 'string' ? source.updatedAt : undefined,
    activeUserCount:
      typeof source.activeUserCount === 'number'
        ? source.activeUserCount
        : typeof source.active_user_count === 'number'
          ? source.active_user_count
          : undefined,
    variants: Array.isArray(variantsRaw) ? variantsRaw.map(normalizePlanDetailVariant) : [],
  };
};

/**
 * Convert camelCase string to snake_case
 */
const toSnakeCase = (str: string): string => {
  // Keep backend price config key as-is.
  if (str === 'taxPercent') {
    return str;
  }
  return str.replace(/([A-Z])/g, '_$1').toLowerCase();
};

/**
 * Transform object keys from camelCase to snake_case recursively
 * Filters out undefined values
 */
const transformToSnakeCase = <T extends Record<string, unknown>>(obj: T): SnakeCaseObject => {
  const result: SnakeCaseObject = {};
  
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) continue;
    
    const snakeKey = toSnakeCase(key);
    
    if (Array.isArray(value)) {
      result[snakeKey] = value.map(item => 
        item && typeof item === 'object' && !(item instanceof Date)
          ? transformToSnakeCase(item as Record<string, unknown>)
          : item
      );
    } else if (value && typeof value === 'object' && !(value instanceof Date)) {
      result[snakeKey] = transformToSnakeCase(value as Record<string, unknown>);
    } else {
      result[snakeKey] = value;
    }
  }
  
  return result;
};

/**
 * Plan Service - Admin Plan Management
 */
export class PlanService extends ApiService {
  async getPlans(): Promise<AdminSubscriptionPlan[]> {
    const response = await this.get<Record<string, unknown>[] | AdminSubscriptionPlan[]>(
      API_ENDPOINTS.plans.list
    );
    const items = Array.isArray(response) ? response : [];
    return items.map((item) =>
      normalizeAdminSubscriptionPlan(item as Record<string, unknown>)
    );
  }

  async getPlanById(id: string): Promise<PlanDetailResponse> {
    const response = await this.get<Record<string, unknown>>(API_ENDPOINTS.plans.get(id));
    return normalizePlanDetailResponse(response);
  }

  async createPlan(planData: CreatePlanRequest): Promise<PlanDetailResponse> {
    const transformedData = transformToSnakeCase(planData as unknown as Record<string, unknown>);
    return this.post<PlanDetailResponse>(API_ENDPOINTS.plans.create, transformedData);
  }

  async updatePlan(id: string, planData: UpdatePlanRequest): Promise<PlanDetailResponse> {
    const transformedData = transformToSnakeCase(planData as unknown as Record<string, unknown>);
    const response = await this.patch<Record<string, unknown>>(
      API_ENDPOINTS.plans.update(id),
      transformedData
    );
    return normalizePlanDetailResponse(response);
  }

  async deletePlan(id: string): Promise<void> {
    return this.delete<void>(API_ENDPOINTS.plans.delete(id));
  }

  async getVariants(planId: string): Promise<PlanVariant[]> {
    return this.get<PlanVariant[]>(API_ENDPOINTS.plans.variants.list(planId));
  }

  async createVariant(
    planId: string,
    variantData: Omit<CreatePlanVariantRequest, 'planId'>
  ): Promise<PlanVariant> {
    return this.post<PlanVariant>(
      API_ENDPOINTS.plans.variants.create(planId),
      variantData
    );
  }

  async updateVariant(
    planId: string,
    variantId: string,
    variantData: UpdatePlanVariantRequest
  ): Promise<PlanVariant> {
    return this.put<PlanVariant>(
      API_ENDPOINTS.plans.variants.update(planId, variantId),
      variantData
    );
  }

  async deleteVariant(planId: string, variantId: string): Promise<void> {
    return this.delete<void>(API_ENDPOINTS.plans.variants.delete(planId, variantId));
  }

  /**
   * Get all plan details for assignment
   * GET /admin/plan/all-details
   */
  async getAllPlanDetails(): Promise<PlanDetailForAssignment[]> {
    return this.get<PlanDetailForAssignment[]>(API_ENDPOINTS.plans.allDetails);
  }

  /**
   * Get plan detail by ID
   * GET /admin/plan/detail/{planDetailId}
   */
  async getPlanDetailById(planDetailId: string): Promise<PlanDetailForAssignment> {
    return this.get<PlanDetailForAssignment>(API_ENDPOINTS.plans.detailById(planDetailId));
  }
}

// Export singleton instance
export const planService = new PlanService();
